import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../app/store';
import { useParams } from 'react-router-dom';
import { createContext, useEffect, useState } from 'react';
import { authenticateUserFromStorage } from '../../../../app/features/sessions/sessionSlice';
import { getGroup } from '../../../../app/features/sessions/sessionGroups/sessionGroup';
import { PostGroupCreate } from '../../../../app/components/forms/post/PostGroupCreate';
import PostsComponent from './PostsComponent';
import { SelectProjectModal } from '../../../../app/components/modal/attachProjectSelectionModal/SelectProjectModal';
import {
    Box,
    Collapse,
    Container,
    Divider,
    IconButton,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const BG_MAIN        = '#15151a';
const BG_SECTION     = '#1c1c24';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

export function Group() {
    const authHeaders    = useSelector((state: RootState) => state.session.authHeaders);
    const userValidated  = useSelector((state: RootState) => state.session.loggedIn);
    const groupName      = useSelector((state: RootState) => state.sessionGroup.name);
    const groupDescription = useSelector((state: RootState) => state.sessionGroup.description);
    const dispatch = useDispatch<AppDispatch>();
    const { userNicknameRoute, groupId } = useParams();
    const [projectSelectedId, setProjectSelectedId] = useState<number | undefined>(undefined);
    const modalOpenContext = createContext(false);
    const [buttonSelectProjectClicked, setButtonSelectProjectClicked] = useState(false);
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);

    useEffect(() => {
        async function authUserOnRoute() {
            if (!userValidated) {
                await dispatch(authenticateUserFromStorage());
            }
        }

        async function getGroupInfo() {
            if (authHeaders && userNicknameRoute && groupId) {
                await dispatch(
                    getGroup({
                        authHeaders: authHeaders,
                        getGroupRouteParams: {
                            userNickname: userNicknameRoute,
                            groupId: groupId,
                        },
                    })
                );
            }
        }

        authUserOnRoute();
        getGroupInfo();
    }, [userValidated]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: { xs: 2, sm: 3 } }}>
            {buttonSelectProjectClicked && (
                <SelectProjectModal
                    onOpenModal={setButtonSelectProjectClicked}
                    setProjectSelectedId={setProjectSelectedId}
                />
            )}

            <Container maxWidth='md'>
                {/* Header do grupo */}
                <Box
                    sx={{
                        bgcolor: BG_SECTION,
                        border: BORDER_SUBTLE,
                        borderRadius: '10px',
                        mb: 2,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            pt: { xs: 2, sm: 2.5 },
                            pb: groupDescription ? 1 : 2.5,
                        }}
                    >
                        <Typography
                            variant='h5'
                            fontWeight={700}
                            color={TEXT_PRIMARY}
                            sx={{
                                fontSize: { xs: '1.1rem', sm: '1.4rem' },
                                wordBreak: 'break-word',
                            }}
                        >
                            {groupName}
                            <Box
                                component='span'
                                sx={{
                                    display: 'inline-block',
                                    width: 32,
                                    height: 3,
                                    bgcolor: BRAND_RED,
                                    borderRadius: 2,
                                    ml: 1.5,
                                    verticalAlign: 'middle',
                                }}
                            />
                        </Typography>
                    </Box>

                    {groupDescription && (
                        <>
                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
                            <Box sx={{ px: { xs: 2, sm: 3 }, py: 1 }}>
                                <Collapse in={descriptionExpanded} collapsedSize={48}>
                                    <Typography
                                        variant='body2'
                                        color={TEXT_SECONDARY}
                                        sx={{
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                        }}
                                    >
                                        {groupDescription}
                                    </Typography>
                                </Collapse>
                                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                                    <IconButton
                                        size='small'
                                        onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                                        sx={{
                                            color: TEXT_SECONDARY,
                                            transform: descriptionExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.25s',
                                        }}
                                    >
                                        <ExpandMoreIcon fontSize='small' />
                                    </IconButton>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                {/* Criar post */}
                <Box sx={{ mb: 2 }}>
                    <PostGroupCreate
                        onOpenModal={setButtonSelectProjectClicked}
                        modalOpened={buttonSelectProjectClicked}
                        projectSelectedId={projectSelectedId}
                        setProjectSelectedId={setProjectSelectedId}
                    />
                </Box>

                {/* Lista de posts */}
                <PostsComponent />
            </Container>
        </Box>
    );
}
